import { Fragment, useEffect, useState } from 'react';

import {
  alpha,
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  makeStyles,
  Typography,
} from '@material-ui/core';
import { Refresh } from '@material-ui/icons';
import clsx from 'clsx';
import { useModal } from 'react-modal-hook';

import DialogHeader from 'components/DialogHeader';
import * as socketEvents from 'constants/socketEvents';
import socket from 'services/socket';
import { CanalInterface, TipoConexao } from 'store/reducers/canais';

import ModalCodeError from './ModalCodeError';

const useStyles = makeStyles(({ spacing, palette, typography }) => ({
  dialogContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing(3),
    padding: spacing(3, 4, 4),
    textAlign: 'center',
  },
  pinContainer: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing(1),
    minHeight: spacing(12),
    paddingBlock: spacing(3),
    backgroundColor: alpha(palette.background.tertiary, 0.5),
    borderRadius: spacing(1),
  },
  pinLetter: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: spacing(5),
    height: spacing(6),
    fontSize: typography.pxToRem(20),
    border: `1px solid ${palette.text.primary}`,
    borderRadius: spacing(1),
  },
  pinLetterWithoutBorder: {
    border: 'none',
    borderRadius: 0,
    width: 'auto',
    height: 'auto',
  },
  pinTimeout: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing(1),
    cursor: 'pointer',
  },
}));

interface ModalPinCodeProps {
  canal?: CanalInterface;
  open: boolean;
  onClose: () => void;
  isProcessarMensagensAntigas: boolean;
}

export default function ModalPinCode({
  open,
  onClose,
  canal,
  isProcessarMensagensAntigas,
}: ModalPinCodeProps) {
  const classes = useStyles();
  const [loadingPinCode, setLoadingPinCode] = useState(true);
  const [pinCode, setPinCode] = useState('');
  const [pinCodeTimeout, setPinCodeTimeout] = useState(false);
  const [pinCodeError, setPinCodeError] = useState('');

  // Após fechar o modal envia o emit cancelando o recebimento do pin code
  const handleExited = () => {
    if (canal) {
      const { canalId } = canal;
      const data = { canalId, action: 'leave' };

      socket.emit(socketEvents.EMIT_ROOM_QR_CODE, data);
    }

    setPinCode('');
    setPinCodeTimeout(false);
    setLoadingPinCode(false);
  };

  // Tenta reconectar o canal
  const handleClickButtonTimeout = () => {
    if (canal) {
      const { canalId } = canal;
      socket.emit(socketEvents.EMIT_RECONECTAR_CANAL, {
        canalId,
        tipoConexao: TipoConexao.WHATSAPP_CODE,
        isProcessarMensagensAntigas,
      });

      setLoadingPinCode(true);
      setPinCodeTimeout(false);
      setPinCode('');
    }
  };

  const [showModalPinCodeError, hideModalPinCodeError] = useModal(
    ({ in: open }) => (
      <ModalCodeError
        open={open}
        onClose={hideModalPinCodeError}
        error={pinCodeError}
      />
    ),
    [pinCodeError],
  );

  useEffect(() => {
    const handleNewPinCode = ({ code }: { code: string }) => {
      setPinCode(code);
      setLoadingPinCode(false);
      setPinCodeTimeout(false);
    };
    const handlePinCodeTimeout = () => {
      setPinCode('');
      setPinCodeTimeout(true);
      setLoadingPinCode(false);
    };
    const handleConectado = () => {
      onClose();
    };
    const handleNaoConectado = ({ message }: { message: string }) => {
      setPinCodeError(message);

      onClose();
      showModalPinCodeError();
    };

    socket.on(socketEvents.ON_WHATSAPP_NEW_CODE, handleNewPinCode);
    socket.on(socketEvents.ON_WHATSAPP_CONECTADO, handleConectado);
    socket.on(socketEvents.ON_WHATSAPP_NAO_CONECTADO, handleNaoConectado);
    socket.on(socketEvents.ON_WHATSAPP_QR_CODE_TIMEOUT, handlePinCodeTimeout);

    return () => {
      socket.off(socketEvents.ON_WHATSAPP_NEW_CODE, handleNewPinCode);
      socket.off(socketEvents.ON_WHATSAPP_CONECTADO, handleConectado);
      socket.off(socketEvents.ON_WHATSAPP_NAO_CONECTADO, handleNaoConectado);
      socket.off(
        socketEvents.ON_WHATSAPP_QR_CODE_TIMEOUT,
        handlePinCodeTimeout,
      );
    };
  }, [onClose, showModalPinCodeError]);

  return (
    <Dialog
      open={open}
      disableEscapeKeyDown
      onClose={(_, reason) => {
        if (reason === 'backdropClick') return;
        onClose();
      }}
      TransitionProps={{ onExited: handleExited }}
      maxWidth="sm"
      fullWidth
    >
      <DialogHeader
        title={
          <Box component="span" style={{ fontWeight: 700 }}>
            Conectar canal
          </Box>
        }
        onClose={onClose}
      />

      <DialogContent className={classes.dialogContent}>
        <Typography variant="body2">Insira o código no WhatsApp</Typography>

        <Box className={classes.pinContainer}>
          {pinCodeTimeout && (
            <Box
              className={classes.pinTimeout}
              onClick={handleClickButtonTimeout}
            >
              <Refresh fontSize="large" />
              <Typography component={Box}>
                CLIQUE PARA RECARREGAR O CÓDIGO
              </Typography>
            </Box>
          )}

          {!pinCodeTimeout && (
            <>
              {loadingPinCode && <CircularProgress />}

              {!loadingPinCode &&
                pinCode &&
                pinCode.split('').map((letter, index) => (
                  <Fragment key={`${letter}-${index + 1}`}>
                    <Box
                      className={clsx(classes.pinLetter, {
                        [classes.pinLetterWithoutBorder]: letter === '-',
                      })}
                    >
                      {letter}
                    </Box>
                  </Fragment>
                ))}
            </>
          )}
        </Box>

        <Typography variant="body2">
          Você está conectando a conta do WhatsApp{' '}
          <strong>{canal?.numeroContato}</strong>
        </Typography>
      </DialogContent>
    </Dialog>
  );
}
